import { Button, Spacer } from '@odekoteam/doppio';
import { useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../hooks/store';
import {
  nightEndSelector,
  selectedWarehouseIdSelector,
} from '../../state/selectors/delivery';
import { modalSelector } from '../../state/selectors/modal';
import {
  getAllStopStatuses,
  getRoutes,
  moveDeliveryStopsToRoute,
} from '../../state/slices/delivery';
import { getPrimaryStopStatuses } from '../../state/slices/delivery/getPrimaryStopStatuses';
import { hideModal } from '../../state/slices/modal';
import CancelButton from './CancelButton';
import { buttonContainer, styledConfirmationModal } from './styles';

export const MOVE_TO_ODEKO_TITLE = 'Move stop to Odeko Deliveries?';
export const MOVE_TO_ODEKO_BODY =
  'This stop moves back onto the Odeko delivery routes and will be delivered by a driver instead of collected at the warehouse.';

export function MoveStopToOdekoDeliveriesModal(): JSX.Element {
  const dispatch = useAppDispatch();
  const nightEnd = useAppSelector(nightEndSelector);
  const { variables } = useAppSelector(modalSelector);
  const selectedWarehouseId = useAppSelector(selectedWarehouseIdSelector);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const stopId: string | undefined = variables?.stopId;
  const lobbyRouteId: string | undefined = variables?.lobbyRouteId;

  const handleOnClickConfirm = async (): Promise<void> => {
    if (!stopId || !lobbyRouteId) {
      dispatch(hideModal());
      return;
    }
    setIsSubmitting(true);
    // Deliberately not suppressing the thunk's own notifications: its error
    // toast carries the backend's typed message, and this move can fail for
    // reasons only the server knows (an already-completed stop, a Metrobi
    // assignment). A local generic toast would hide exactly that.
    const result = await dispatch(
      moveDeliveryStopsToRoute({
        stopIds: [stopId],
        routeId: lobbyRouteId,
        redelivery: false,
      }),
    );

    if (result.payload && selectedWarehouseId && nightEnd?.viewing) {
      // The bucket loses a stop and the Lobby gains one, and the row status
      // icons read stop-status caches that are separate queries from
      // state.routes. Refresh all three, the way bulk-complete does.
      const input = {
        warehouseId: selectedWarehouseId,
        nightEnd: nightEnd.viewing,
      };
      dispatch(getRoutes(input));
      dispatch(getAllStopStatuses(input));
      dispatch(getPrimaryStopStatuses(input));
    }

    setIsSubmitting(false);
    dispatch(hideModal());
  };

  return (
    <div
      id="move-stop-to-odeko-deliveries-modal"
      sx={{ ...styledConfirmationModal, textAlign: 'left' }}
    >
      <div sx={{ variant: 'text.headingSm' }}>{MOVE_TO_ODEKO_TITLE}</div>
      <Spacer height="$3" />
      <div sx={{ variant: 'text.paragraphMdDefault', color: 'secondaryText' }}>
        {MOVE_TO_ODEKO_BODY}
      </div>
      <div sx={{ ...buttonContainer, justifyContent: 'flex-start' }}>
        <Button
          variant="primary"
          id="move-stop-to-odeko-deliveries-action-button"
          data-test-id="move-stop-to-odeko-deliveries-action-button"
          onPress={handleOnClickConfirm}
          isDisabled={isSubmitting}
        >
          {isSubmitting ? 'Moving...' : 'Move to Odeko Deliveries'}
        </Button>
        <Spacer width="$2" />
        <CancelButton variant="secondary" />
      </div>
    </div>
  );
}
